import React, { useEffect, useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SurveyContext } from "../context/SurveyContext";
import { AuthContext } from "../context/AuthContext";
import api from "../services/api";
import { FaSpinner } from "react-icons/fa";

const ConditionInitializer = ({ children }) => {
  const location = useLocation();
  const {
    instanceId,
    conditionId,
    updateInstance,
    updateCondition,
    updateParticipantInfo,
    updateExperimentalFactors,
    setInitialized,
    clearSession,
    resetOnboarding,
  } = useContext(SurveyContext);
  const { logout } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [showConditionSelector, setShowConditionSelector] = useState(false);
  const [showReturningUserFlow, setShowReturningUserFlow] = useState(false);
  const [availableConditions, setAvailableConditions] = useState([]);
  const [selectedCondition, setSelectedCondition] = useState("");
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  // Check if this is an admin, study, or root route - bypass condition initialization
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isStudyRoute = location.pathname === "/study";
  const isRootRoute = location.pathname === "/";

  // Check if this is a returning user going to login
  const searchParams = new URLSearchParams(location.search);
  const isReturningUserLogin = location.pathname === "/login" &&
    searchParams.get("returning") === "true" &&
    searchParams.get("c"); // Must have conditionId

  useEffect(() => {
    if (isAdminRoute || isStudyRoute || isRootRoute) {
      // Admin, study, and root routes don't need condition/instance initialization
      setLoading(false);
      setShowConditionSelector(false);
      return;
    }
    if (isReturningUserLogin) {
      // Returning users going to login don't need instance initialization
      setLoading(false);
      setShowConditionSelector(false);
      setInitialized(true);
      return;
    }
    // Study mode: StudyFlow already configured SurveyContext (instanceId, conditionId, etc.)
    // before navigating here. Skip re-initialization to avoid loading flash, redundant fetches,
    // and the risk of clearSession() wiping state if the instance fetch fails.
    if (window.__studyMode) {
      setLoading(false);
      setShowConditionSelector(false);
      return;
    }

    // On page refresh during study, window.__studyMode is lost but the study return
    // URL is persisted in localStorage. Redirect back to the study flow.
    const studyReturnUrl = sessionStorage.getItem("spiders_study_return_url");
    if (studyReturnUrl) {
      window.location.href = studyReturnUrl;
      return;
    }
    initializeSession();
    // Note: location.pathname is intentionally excluded — in-app navigation
    // (e.g. /newsfeed → /profile → /newsfeed) must NOT trigger re-initialization.
    // Session init only needs to run on first mount, when URL search params change
    // (new session), or when switching to/from admin routes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, isAdminRoute, isStudyRoute, isRootRoute, isReturningUserLogin]);

  const initializeSession = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams(location.search);

      // Extract URL parameters
      const urlCondition = params.get("c");
      const urlParticipantId = params.get("i");
      const urlCaseNumber = params.get("cn");

      // Extract experimental factors (any other params)
      const experimentalFactors = {};
      const reservedParams = ["c", "i", "cn"];
      for (const [key, value] of params.entries()) {
        if (!reservedParams.includes(key)) {
          experimentalFactors[key] = value;
        }
      }

      // If we have URL parameters, check if this is a new session
      if (urlParticipantId && urlCondition) {
        // Check if we already have a session for this participant
        const storedSession = sessionStorage.getItem("spiders_session");
        let existingSession = null;

        if (storedSession) {
          try {
            existingSession = JSON.parse(storedSession);
          } catch (e) {
            console.error("Error parsing stored session:", e);
          }
        }

        // If different participant, clear old session
        if (existingSession && existingSession.participantId !== urlParticipantId) {
          clearSession();
        }

        // Update participant info
        updateParticipantInfo(urlParticipantId, urlCaseNumber);
        updateExperimentalFactors(experimentalFactors);

        // Create or resume instance
        await createOrResumeInstance(urlCondition, urlParticipantId, urlCaseNumber, experimentalFactors);
      } else if (urlCondition) {
        // Condition provided but no participant - standalone test mode
        updateCondition(urlCondition);
        setShowConditionSelector(false);

        // Check if we have an existing instance for this condition
        const storedSession = sessionStorage.getItem("spiders_session");
        if (storedSession) {
          try {
            const session = JSON.parse(storedSession);
            if (session.conditionId === urlCondition && session.instanceId) {
              // Resume existing instance
              const { data } = await api.get(`/instances/${session.instanceId}`);
              updateInstance(data);
              setShowConditionSelector(false);
              setInitialized(true);
              setLoading(false);
              return;
            }
          } catch (e) {
            console.error("Error resuming session:", e);
          }
        }

        // No existing instance, show selector or create test instance
        await createOrResumeInstance(urlCondition, null, null, experimentalFactors, true);
      } else if (instanceId) {
        // No URL params but we have an existing instance - resume
        try {
          const { data } = await api.get(`/instances/${instanceId}`);
          updateInstance(data);
          setShowConditionSelector(false);
          setInitialized(true);
        } catch (e) {
          console.error("Error resuming instance:", e);
          clearSession();
          await loadAvailableConditions();
        }
      } else {
        // No URL params, no existing instance - standalone mode
        await loadAvailableConditions();
      }
    } catch (err) {
      console.error("Error initializing session:", err);
      setError(err.response?.data?.message || err.message || "Failed to initialize session");
      setInitialized(true, err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableConditions = async () => {
    try {
      const { data } = await api.get("/conditions");
      setAvailableConditions(data.conditions || []);
      setShowConditionSelector(true);
      setInitialized(true);
    } catch (err) {
      console.error("Error loading conditions:", err);
      setError("Failed to load available conditions");
      setInitialized(true, "Failed to load conditions");
    }
  };

  const createOrResumeInstance = async (
    conditionId,
    participantId,
    caseNumber,
    experimentalFactors,
    isTest = false
  ) => {
    try {
      const { data } = await api.post("/instances", {
        conditionId,
        participantId,
        caseNumber,
        experimentalFactors,
        isTest,
      });

      // If a new instance was created (not resumed), ensure onboarding state is reset
      // This handles URL-based flows where clearSession might not have been called
      if (data.created) {
        // Reset onboarding-related state for fresh start
        resetOnboarding();
      }

      updateInstance(data.instance);
      updateCondition(conditionId);
      setShowConditionSelector(false);
      setInitialized(true);

      console.log(
        data.created ? "Created new instance:" : "Resumed existing instance:",
        data.instance._id
      );
    } catch (err) {
      console.error("Error creating/resuming instance:", err);
      throw err;
    }
  };

  const handleConditionSelect = async () => {
    if (!selectedCondition) return;

    setLoading(true);
    setError(null);

    try {
      // Clear any existing auth and session data before creating a new instance
      // This ensures the user must register fresh for the new condition
      await logout();
      clearSession();

      await createOrResumeInstance(selectedCondition, null, null, {}, true);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create session");
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-spiders-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-spiders-primary-950 flex items-center justify-center">
            <FaSpinner className="w-8 h-8 text-spiders-primary-400 animate-spin" />
          </div>
          <p className="text-spiders-text-secondary">Initializing...</p>
        </div>
      </div>
    );
  }

  // Handle returning user - go directly to login
  const handleReturningUser = () => {
    if (!selectedCondition) return;
    updateCondition(selectedCondition);
    setShowReturningUserFlow(true);
    setShowConditionSelector(false);
    setInitialized(true);
    // Navigate to login without creating an instance, include conditionId in URL
    navigate(`/login?returning=true&c=${selectedCondition}`);
  };

  // Condition selector for standalone mode
  if (showConditionSelector) {
    return (
      <div className="min-h-screen bg-spiders-bg flex items-center justify-center">
        <div className="spiders-card p-8 max-w-md w-full mx-4 relative overflow-hidden">
          {/* Decorative gradient */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-spiders-primary-500 via-spiders-accent-400 to-spiders-primary-500"></div>

          {/* Logo */}
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-gradient-primary mb-2">SPIDERS</h1>
            <p className="text-spiders-text-secondary text-sm">
              Select a condition to begin your session
            </p>
          </div>

          {error && (
            <div className="bg-spiders-error/20 border border-spiders-error text-spiders-error p-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          {availableConditions.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-spiders-bg-elevated flex items-center justify-center">
                <svg className="w-8 h-8 text-spiders-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <p className="text-spiders-text-secondary mb-2">No conditions available</p>
              <p className="text-spiders-text-muted text-sm">
                Please contact the research administrator or wait for a condition to be activated.
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-3 mb-6">
                {availableConditions.map((cond) => (
                  <button
                    key={cond.id}
                    onClick={() => setSelectedCondition(cond.id)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${
                      selectedCondition === cond.id
                        ? "border-spiders-primary-500 bg-spiders-primary-950/50 shadow-spiders"
                        : "border-spiders-border bg-spiders-bg-surface hover:border-spiders-border-light"
                    }`}
                  >
                    <p className="text-spiders-text-primary font-semibold">{cond.name}</p>
                    {cond.topicName && (
                      <p className="text-spiders-text-secondary text-sm mt-1">Topic: {cond.topicName}</p>
                    )}
                    {cond.description && (
                      <p className="text-spiders-text-muted text-xs mt-1">{cond.description}</p>
                    )}
                  </button>
                ))}
              </div>

              <button
                onClick={handleConditionSelect}
                disabled={!selectedCondition || loading}
                className={`w-full py-3 rounded-lg font-semibold transition-all duration-200 ${
                  selectedCondition && !loading
                    ? "spiders-btn-primary"
                    : "bg-spiders-bg-elevated text-spiders-text-muted cursor-not-allowed"
                }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <FaSpinner className="animate-spin" />
                    Creating Session...
                  </span>
                ) : (
                  "New Participant"
                )}
              </button>

              <button
                onClick={handleReturningUser}
                disabled={!selectedCondition || loading}
                className={`w-full py-3 mt-3 rounded-lg font-semibold transition-all duration-200 ${
                  selectedCondition && !loading
                    ? "spiders-btn-secondary"
                    : "bg-spiders-bg-dark text-spiders-text-muted cursor-not-allowed"
                }`}
              >
                I Already Have an Account
              </button>
            </>
          )}

          <p className="text-spiders-text-muted text-xs text-center mt-6">
            This is a test session. For official participation, use the link provided by the research team.
          </p>
        </div>
      </div>
    );
  }

  // Render children if we have a condition/instance
  return children;
};

export default ConditionInitializer;
